/**
 * Retry Logic with Exponential Backoff
 * 
 * Provides utilities for retrying failed API requests with:
 * - Exponential backoff (1s → 2s → 4s → ...)
 * - Jitter to prevent thundering herd
 * - Configurable retry attempts
 * - Smart retry detection (only retry transient failures)
 * 
 * @module retryLogic
 */

import { RETRY_CONFIG, IS_PRODUCTION } from '../constants';

/**
 * Calculate delay for next retry attempt
 * Uses exponential backoff with jitter
 * 
 * Formula: delay = min(INITIAL_DELAY * (BACKOFF_MULTIPLIER ^ attempt) + jitter, MAX_DELAY)
 * 
 * @param attemptNumber - Current attempt number (1-based)
 * @returns Delay in milliseconds
 * 
 * @example
 * ```typescript
 * const delay1 = calculateRetryDelay(1); // ~1000ms (1s + jitter)
 * const delay2 = calculateRetryDelay(2); // ~2000ms (2s + jitter)
 * const delay3 = calculateRetryDelay(3); // ~4000ms (4s + jitter)
 * ```
 */
export function calculateRetryDelay(attemptNumber: number): number {
  const baseDelay = RETRY_CONFIG.INITIAL_DELAY_MS * 
    Math.pow(RETRY_CONFIG.BACKOFF_MULTIPLIER, attemptNumber - 1);
  
  // Add jitter (0-1000ms) to prevent thundering herd
  const jitter = Math.random() * 1000;
  
  // Cap at MAX_DELAY_MS
  return Math.min(baseDelay + jitter, RETRY_CONFIG.MAX_DELAY_MS);
}

/**
 * Check if HTTP status code indicates a retryable error
 * 
 * Retryable status codes:
 * - 408 Request Timeout
 * - 500 Internal Server Error
 * - 502 Bad Gateway
 * - 503 Service Unavailable
 * - 504 Gateway Timeout
 * 
 * @param status - HTTP status code (undefined for network errors)
 * @returns true if error should be retried
 * 
 * @example
 * ```typescript
 * isRetryableStatus(500); // true
 * isRetryableStatus(404); // false
 * isRetryableStatus(undefined); // true (network error)
 * ```
 */
export function isRetryableStatus(status?: number): boolean {
  // Network errors (no status) are retryable
  if (!status) {
    return true;
  }
  
  // Check if status code is in retryable list
  return RETRY_CONFIG.RETRYABLE_STATUS_CODES.includes(
    status as typeof RETRY_CONFIG.RETRYABLE_STATUS_CODES[number]
  );
}

/**
 * Sleep for specified duration
 * 
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after delay
 * 
 * @example
 * ```typescript
 * await sleep(1000); // Wait 1 second
 * ```
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute function with retry logic
 * 
 * Automatically retries the function up to `maxRetries` times if it fails.
 * Uses exponential backoff with jitter between retries.
 * 
 * @param fn - Function to execute
 * @param maxRetries - Maximum number of retry attempts (default: RETRY_CONFIG.MAX_RETRIES)
 * @param shouldRetry - Function to determine if error should be retried (default: always retry)
 * @returns Result of function
 * @throws Last error if all retry attempts fail
 * 
 * @example
 * ```typescript
 * // Retry any error up to 3 times
 * const result = await withRetry(async () => {
 *   return await fetch('/api/data');
 * });
 * 
 * // Retry only specific errors
 * const result = await withRetry(
 *   async () => fetchData(),
 *   3,
 *   (error) => error instanceof NetworkError
 * );
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = RETRY_CONFIG.MAX_RETRIES,
  shouldRetry: (error: unknown) => boolean = () => true
): Promise<T> {
  let lastError: unknown;
  
  // Attempt 1 is the initial try, attempts 2-4 are retries
  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry if this was the last attempt
      if (attempt > maxRetries) {
        break;
      }
      
      // Don't retry if error is not retryable
      if (!shouldRetry(error)) {
        break;
      }
      
      // Calculate delay and wait
      const delay = calculateRetryDelay(attempt);
      
      if (!IS_PRODUCTION) {
        console.log(
          `[ApiClient] Retry attempt ${attempt}/${maxRetries} after ${delay.toFixed(0)}ms`,
          error instanceof Error ? error.message : error
        );
      }
      
      await sleep(delay);
    }
  }
  
  // All retries failed, throw last error
  throw lastError;
}

/**
 * Check if error should be retried based on status code
 * 
 * Helper function for use with `withRetry` shouldRetry parameter.
 * 
 * @param error - Error to check
 * @returns true if error has a retryable status code
 * 
 * @example
 * ```typescript
 * const result = await withRetry(
 *   async () => apiCall(),
 *   3,
 *   shouldRetryBasedOnStatus
 * );
 * ```
 */
export function shouldRetryBasedOnStatus(error: unknown): boolean {
  // Extract status from different error types
  let status: number | undefined;
  
  if (error && typeof error === 'object') {
    if ('status' in error && typeof error.status === 'number') {
      status = error.status;
    } else if ('httpStatus' in error && typeof error.httpStatus === 'number') {
      status = error.httpStatus;
    }
  }
  
  return isRetryableStatus(status);
}
